import React, { useEffect, useMemo, useState } from 'react';
import { Loan, FilterState, SortState, SortField, Role, Manager } from './types/loan';
import { mockLoans, mockManagers } from './utils/mockData';
import { FilterControls } from './components/FilterControls';
import { SummaryCards } from './components/SummaryCards';
import { LoanTable } from './components/LoanTable';
import { ManagerDashboard } from './components/ManagerDashboard';
import { ActionModal } from './components/ActionModal';
import { Leaderboard } from './components/Leaderboard';
import { CheckIcon, XIcon, ArrowLeftIcon, ListIcon, ClockIcon, RotateCcwIcon } from 'lucide-react';
export function App() {
  // State
  const [role, setRole] = useState<Role>('Admin');
  const [viewingLO, setViewingLO] = useState<string | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<string | null>(null);
  // Logged in user simulation for Loan Officer view
  const [loggedInLO, setLoggedInLO] = useState<string>('Sarah Jenkins');
  const [filters, setFilters] = useState<FilterState>({
    currentMarketRate: 6.0,
    startDate: '2020-01-01',
    endDate: new Date().toISOString().split('T')[0],
    rateThreshold: 1.0
  });
  const [selectedLoanIds, setSelectedLoanIds] = useState<Set<string>>(new Set());
  const [sortState, setSortState] = useState<SortState>({
    field: 'rateDifference',
    direction: 'desc'
  });
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [actionModalLoan, setActionModalLoan] = useState<Loan | null>(null);
  // Generated list state — what Managers/LOs actually see
  const [generatedLoans, setGeneratedLoans] = useState<Loan[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [generatedMarketRate, setGeneratedMarketRate] = useState<number>(6.0);
  // Clear toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);
  // Reset view when role changes
  useEffect(() => {
    setViewingLO(null);
    setSelectedLoanIds(new Set());
    setSelectedManagerId(null);
  }, [role]);
  // Clear LO drill-down when manager changes
  useEffect(() => {
    setViewingLO(null);
  }, [selectedManagerId]);
  // Handlers
  const handleApplyFilters = () => {
    setSelectedLoanIds(new Set());
  };
  const handleSort = (field: SortField) => {
    setSortState(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  const handleToggleSelection = (loanId: string) => {
    setSelectedLoanIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  };
  const handleToggleAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedLoanIds(new Set(filteredAndSortedLoans.map(l => l.id)));
    } else {
      setSelectedLoanIds(new Set());
    }
  };
  const handleTakeAction = (loan: Loan) => {
    setActionModalLoan(loan);
  };
  const handleLogCall = (loan: Loan) => {
    setToast({
      message: `Call logged for ${loan.borrowerName}`,
      type: 'success'
    });
    setActionModalLoan(null);
  };
  const handleCloneOpportunity = (loan: Loan) => {
    setToast({
      message: `Opportunity cloned for ${loan.borrowerName} (${loan.id})`,
      type: 'success'
    });
    setActionModalLoan(null);
  };
  const handleSetTargetRate = (loan: Loan, rate: number) => {
    setGeneratedLoans(prev => prev.map(l => l.id === loan.id ? {
      ...l,
      targetRate: rate
    } : l));
    setToast({
      message: `Target rate set to ${rate.toFixed(3)}% for ${loan.borrowerName}`,
      type: 'success'
    });
    setActionModalLoan(null);
  };
  const handleResetFilters = () => {
    setFilters({
      currentMarketRate: 6.0,
      startDate: '2020-01-01',
      endDate: new Date().toISOString().split('T')[0],
      rateThreshold: 1.0
    });
    setSelectedLoanIds(new Set());
    setToast({
      message: 'Filters reset to defaults',
      type: 'success'
    });
  };
  const handleGenerateList = () => {
    if (filteredAndSortedLoans.length === 0) {
      setToast({
        message: 'No loans match the current filters. Adjust filters and try again.',
        type: 'error'
      });
      return;
    }
    setGeneratedLoans([...filteredAndSortedLoans]);
    setGeneratedMarketRate(filters.currentMarketRate);
    setGeneratedAt(new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }));
    setToast({
      message: `Configuration saved with ${filteredAndSortedLoans.length} opportunities! Now visible to Managers and Loan Officers.`,
      type: 'success'
    });
  };
  // Admin preview — live filtered data
  const filteredAndSortedLoans = useMemo(() => {
    let result = mockLoans.filter(loan => {
      const rateDiff = loan.interestRate - filters.currentMarketRate;
      const meetsThreshold = rateDiff >= filters.rateThreshold;
      const afterStart = loan.closeDate >= filters.startDate;
      const beforeEnd = loan.closeDate <= filters.endDate;
      return meetsThreshold && afterStart && beforeEnd;
    });
    result.sort((a, b) => {
      let valA: any = a[sortState.field as keyof Loan];
      let valB: any = b[sortState.field as keyof Loan];
      if (sortState.field === 'rateDifference') {
        valA = a.interestRate - filters.currentMarketRate;
        valB = b.interestRate - filters.currentMarketRate;
      }
      if (valA < valB) return sortState.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [filters, sortState]);
  // Selected manager object
  const selectedManager = useMemo(() => mockManagers.find(m => m.id === selectedManagerId) ?? null, [selectedManagerId]);
  // What Managers and LOs actually see — from the generated list
  const visibleLoans = useMemo(() => {
    let result = [...generatedLoans];
    // Role-based filtering on the generated list
    if (role === 'Loan Officer') {
      result = result.filter(loan => loan.loanOfficer === loggedInLO);
    } else if (role === 'Manager') {
      // Filter by selected manager's loan officers first
      if (selectedManager) {
        result = result.filter(loan => selectedManager.loanOfficers.includes(loan.loanOfficer));
      }
      // Then filter by specific LO if drilled in
      if (viewingLO) {
        result = result.filter(loan => loan.loanOfficer === viewingLO);
      }
    }
    // Sort
    result.sort((a, b) => {
      let valA: any = a[sortState.field as keyof Loan];
      let valB: any = b[sortState.field as keyof Loan];
      if (sortState.field === 'rateDifference') {
        valA = a.interestRate - generatedMarketRate;
        valB = b.interestRate - generatedMarketRate;
      }
      if (valA < valB) return sortState.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [generatedLoans, generatedMarketRate, sortState, role, viewingLO, selectedManager]);
  const renderContent = () => {
    // Admin View: High-level metrics + Generate List
    if (role === 'Admin') {
      return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-0">
          <SummaryCards loans={filteredAndSortedLoans} currentMarketRate={filters.currentMarketRate} data-id="element-1" />

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2">
            <div data-id="element-3">
              {generatedAt && <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-4">
                  <ClockIcon className="w-4 h-4" data-id="element-5" />
                  <span data-id="element-6">
                    Last saved:{' '}
                    <span className="font-medium text-gray-700" data-id="element-7">
                      {generatedAt}
                    </span>
                    <span className="ml-1 text-gray-400" data-id="element-8">
                      ({generatedLoans.length} loans)
                    </span>
                  </span>
                </div>}
              {!generatedAt && <p className="text-sm text-gray-400" data-id="element-9">
                  No configuration has been saved yet.
                </p>}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-10">
              <button onClick={handleResetFilters} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 h-10 rounded-lg text-sm font-medium transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 shadow-sm" data-id="element-11">
                <RotateCcwIcon className="w-4 h-4" data-id="element-12" />
                Reset
              </button>

              <button onClick={handleGenerateList} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-cyan-500 hover:brightness-110 text-white px-5 h-10 rounded-lg text-sm font-medium transition-all shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-violet-500" data-id="element-13">
                <ListIcon className="w-4 h-4" data-id="element-14" />
                Save Configuration ({filteredAndSortedLoans.length})
              </button>
            </div>
          </div>

          <Leaderboard loans={filteredAndSortedLoans} currentMarketRate={filters.currentMarketRate} data-id="element-15" />
        </div>;
    }
    // If no list has been generated yet, show empty state for Manager/LO
    if (generatedLoans.length === 0) {
      return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-16">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-17">
            <div className="text-gray-300 mb-4" data-id="element-18">
              <ListIcon className="w-12 h-12 mx-auto" data-id="element-19" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2" data-id="element-20">
              No Opportunities Available Yet
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto" data-id="element-21">
              The Admin has not generated a refinance opportunity list yet. Once
              generated, your applicable opportunities will appear here.
            </p>
          </div>
        </div>;
    }
    // Manager View: KPIs + per-officer breakdown
    if (role === 'Manager' && !viewingLO) {
      return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-22">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-23">
            {generatedAt && <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-24">
                <ClockIcon className="w-4 h-4" data-id="element-25" />
                <span data-id="element-26">
                  List generated:{' '}
                  <span className="font-medium text-gray-700" data-id="element-27">
                    {generatedAt}
                  </span>
                </span>
              </div>}
            <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-28">
              <label className="text-sm font-medium text-gray-500 whitespace-nowrap" data-id="element-29">
                Manager:
              </label>
              <select value={selectedManagerId ?? ''} onChange={e => setSelectedManagerId(e.target.value || null)} className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none cursor-pointer w-full sm:w-auto" data-id="element-30">
                <option value="" data-id="element-31">All Managers</option>
                {mockManagers.map(mgr => <option key={mgr.id} value={mgr.id} data-id="element-32">
                    {mgr.name} ({mgr.loanOfficers.length} officers)
                  </option>)}
              </select>
            </div>
          </div>
          <SummaryCards loans={visibleLoans} currentMarketRate={generatedMarketRate} data-id="element-33" />
          <ManagerDashboard loans={visibleLoans} currentMarketRate={generatedMarketRate} onSelectLO={loName => {
          setLoggedInLO(loName);
          setRole('Loan Officer');
        }} managerName={selectedManager?.name ?? null} data-id="element-34" />
        </div>;
    }
    // Loan Officer view
    return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-35">
        {generatedAt && <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-36">
            <ClockIcon className="w-4 h-4" data-id="element-37" />
            <span data-id="element-38">
              List generated:{' '}
              <span className="font-medium text-gray-700" data-id="element-39">{generatedAt}</span>
            </span>
          </div>}

        <SummaryCards loans={visibleLoans} currentMarketRate={generatedMarketRate} data-id="element-40" />

        <div className="space-y-3" data-id="element-41">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-42">
            <h2 className="text-lg font-semibold text-gray-800" data-id="element-43">
              {loggedInLO}'s Opportunities
              <span className="text-gray-500 font-normal ml-2" data-id="element-44">
                ({visibleLoans.length})
              </span>
              {selectedLoanIds.size > 0 && <span className="text-violet-600 ml-2 text-sm font-normal" data-id="element-45">
                  {selectedLoanIds.size} selected
                </span>}
            </h2>
          </div>

          <LoanTable loans={visibleLoans} currentMarketRate={generatedMarketRate} selectedLoanIds={selectedLoanIds} onToggleSelection={handleToggleSelection} onToggleAll={handleToggleAll} sortState={sortState} onSort={handleSort} onTakeAction={handleTakeAction} role={role} data-id="element-46" />
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-gray-50 text-gray-900 font-sans pb-12" data-id="element-47">
      {/* Header */}
      <header className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-48">
        <div className="mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between" data-id="element-49">
          <div className="flex items-center gap-4" data-id="element-50">
            <img src="https://cdn.magicpatterns.com/uploads/2jfwCeyscqPo2Wm8HxYrkc/Zoom_Logo_New.png" alt="Zoom Home Lending" className="h-9" data-id="element-51" />
            <div className="hidden sm:block w-px h-6 bg-gray-200" data-id="element-52"></div>
            <span className="hidden sm:block text-xs uppercase tracking-wide text-gray-400 font-medium" data-id="element-53">
              Refinance Opportunity Finder
            </span>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-0.5" data-id="element-54">
            {(['Admin', 'Manager', 'Loan Officer'] as Role[]).map(r => <button key={r} onClick={() => setRole(r)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${role === r ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-55">
                {r}
              </button>)}
          </div>
        </div>
        <div className="h-0.5 w-full bg-gradient-to-r from-violet-600 via-blue-500 to-cyan-400" data-id="element-56"></div>
      </header>

      <main className="mx-auto px-4 sm:px-6 lg:px-10 py-8" data-id="element-57">
        {/* Toast Notification */}
        {toast && <div className={`fixed top-20 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl border animate-toast-in ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-id="element-58">
            {toast.type === 'success' ? <CheckIcon className="w-5 h-5 text-green-500" data-id="element-59" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-60" />}
            <span className="font-medium" data-id="element-61">{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600" data-id="element-62">
              <XIcon className="w-4 h-4" data-id="element-63" />
            </button>
          </div>}

        {/* Filters - Admin only */}
        {role === 'Admin' && <FilterControls filters={filters} onFilterChange={setFilters} onApplyFilters={handleApplyFilters} data-id="element-64" />}

        {/* Dynamic Content based on Role */}
        {renderContent()}

        {/* Action Modal */}
        <ActionModal isOpen={!!actionModalLoan} loan={actionModalLoan} onClose={() => setActionModalLoan(null)} onLogCall={handleLogCall} onCloneOpportunity={handleCloneOpportunity} onSetTargetRate={handleSetTargetRate} data-id="element-65" />
      </main>
    </div>;
}